import React, { useState, useEffect } from 'react';
import { Save, X, Plus, Trash2, Search, Calendar, Calculator } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { saleService } from '../../services/saleService';
import { orderService } from '../../services/orderService';
import { productService } from '../../services/productService';
import { clientService } from '../../services/clientService';
import { notificationService } from '../../services/notificationService';
import { SaleFormData, SaleItem, SaleWithDetails } from '../../types/sale';
import { Product } from '../../types/product';
import { OrderWithDetails } from '../../types/order';
import { Customer } from '../../types/customer';
import { supabase } from '../../lib/supabase';

// Exportar para que sea considerado un módulo
export {};

interface SaleFormProps {
  sale?: SaleWithDetails | null;
  onSave: () => void;
  onCancel: () => void;
  orderId?: number; // Para crear venta desde una orden
}

const SaleForm = ({ sale, onSave, onCancel, orderId }: SaleFormProps) => {
  const [formData, setFormData] = useState<SaleFormData>({
    sale_number: '',
    customer_name: '',
    customer_id: undefined,
    order_id: undefined,
    items: [],
    currency: 'USD',
    payment_method: 'Efectivo',
    payment_terms: 'Contado',
    payment_type: 'contado',
    credit_days: 0,
    credit_due_date: '',
    delivery_date: '',
    delivery_days: 5,
    notes: '',
    sale_date: new Date().toISOString().split('T')[0],
    detraccion_enabled: false,
    detraccion_percentage: 4,
    detraccion_amount: 0
  });
  const [products, setProducts] = useState<Product[]>([]);
  const [orders, setOrders] = useState<OrderWithDetails[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchProductTerm, setSearchProductTerm] = useState('');
  const [searchOrderTerm, setSearchOrderTerm] = useState('');
  const [searchCustomerTerm, setSearchCustomerTerm] = useState('');
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [showOrderSearch, setShowOrderSearch] = useState(false);
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);
  const [itemQuantity, setItemQuantity] = useState(1);
  const [itemSalePrice, setItemSalePrice] = useState(0);

  useEffect(() => {
    loadProducts();
    loadOrders();
    loadCustomers();
    
    if (sale) {
      // Modo edición
      setFormData({
        sale_number: sale.sale_number,
        customer_name: sale.customer_name,
        customer_id: sale.customer_id,
        order_id: sale.order_id,
        order_number: sale.order_number || '', // Cargar número de orden si existe
        order_date: sale.order_date || '', // Cargar fecha de orden si existe
        items: [{
          product_id: sale.product.id,
          product_name: sale.product.name,
          quantity: sale.quantity,
          unit_sale_price: sale.unit_sale_price,
          unit_cost_price: sale.unit_cost_price,
          subtotal: sale.subtotal,
          total_cost: sale.total_cost,
          net_profit: sale.net_profit,
          profit_margin: sale.profit_margin
        }],
        currency: sale.currency,
        payment_method: sale.payment_method,
        payment_terms: sale.payment_terms,
        payment_type: sale.payment_terms.toLowerCase().includes('crédito') || sale.payment_terms.toLowerCase().includes('credito') ? 'credito' : 'contado',
        credit_days: sale.credit_days || 0,
        credit_due_date: sale.credit_due_date || '',
        delivery_date: sale.delivery_date || '',
        delivery_days: 5,
        notes: sale.notes || '',
        sale_date: sale.sale_date,
        detraccion_enabled: false,
        detraccion_percentage: 4,
        detraccion_amount: 0
      });
      setSelectedProduct(sale.product);
      
      // Si la venta tiene una orden asociada pero no tiene order_number/order_date,
      // intentar cargar los datos de la orden
      if (sale.order_id && (!sale.order_number || !sale.order_date)) {
        loadOrderDataForExistingSale(sale.order_id);
      }
    } else if (orderId) {
      // Modo creación desde orden
      loadOrderData(orderId);
    }
  }, [sale, orderId]);

  const loadProducts = async () => {
    try {
      const data = await productService.getProducts();
      setProducts(data);
    } catch (error) {
      console.error('Error al cargar productos:', error);
    }
  };

  const loadOrders = async () => {
    try {
      const data = await orderService.getOrders();
      setOrders(data);
    } catch (error) {
      console.error('Error al cargar órdenes:', error);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await clientService.getCustomers();
      setCustomers(data);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
    }
  };

  const loadOrderData = async (id: number) => {
    try {
      const orderData = await saleService.getSaleDataFromOrder(id);
      if (orderData) {
        const order = orders.find(o => o.id === id);
        if (order) {
          // Obtener precio de costo del producto
          const costPrice = await getProductCostPrice(orderData.product_id);
          
          const subtotal = orderData.unit_sale_price * orderData.quantity;
          const totalCost = costPrice * orderData.quantity;
          const netProfit = subtotal - totalCost;
          const profitMargin = subtotal > 0 ? (netProfit / subtotal) * 100 : 0;
          
          setFormData({
            sale_number: '',
            customer_name: orderData.customer_name,
            customer_id: orderData.customer_id,
            order_id: orderData.order_id,
            order_number: orderData.order_number, // Agregar número de orden
            order_date: orderData.order_date, // Agregar fecha de orden
            items: [{
              product_id: orderData.product_id,
              product_name: order.product.name,
              quantity: orderData.quantity,
              unit_sale_price: orderData.unit_sale_price,
              unit_cost_price: costPrice,
              subtotal: subtotal,
              total_cost: totalCost,
              net_profit: netProfit,
              profit_margin: profitMargin
            }],
            currency: orderData.currency,
            payment_method: 'Efectivo',
            payment_terms: orderData.payment_terms || 'Contado',
            payment_type: (orderData.payment_terms || 'Contado').toLowerCase().includes('crédito') || (orderData.payment_terms || 'Contado').toLowerCase().includes('credito') ? 'credito' : 'contado',
            credit_days: orderData.credit_days || 0,
            credit_due_date: '',
            delivery_date: '', // No incluir fecha de entrega en ventas generadas desde órdenes
            delivery_days: 5, // Valor por defecto, no se usará en ventas desde órdenes
            notes: '',
            sale_date: new Date().toISOString().split('T')[0],
            detraccion_enabled: false,
            detraccion_percentage: 4,
            detraccion_amount: 0
          });
          
          const product = products.find(p => p.id === orderData.product_id);
          if (product) {
            setSelectedProduct(product);
          }
        }
      }
    } catch (error) {
      console.error('Error al cargar datos de orden:', error);
      notificationService.error('Error', 'No se pudieron cargar los datos de la orden.');
    }
  };

  const loadOrderDataForExistingSale = async (orderId: number) => {
    try {
      // Solo cargar los datos básicos de la orden para mostrar en el formulario
      const order = orders.find(o => o.id === orderId);
      if (order) {
        setFormData(prev => ({
          ...prev,
          order_number: order.order_number,
          order_date: order.order_date
        }));
        console.log('📋 Datos de orden cargados para venta existente:', {
          order_number: order.order_number,
          order_date: order.order_date
        });
      }
    } catch (error) {
      console.error('Error al cargar datos de orden para venta existente:', error);
    }
  };

  const getProductCostPrice = async (productId: number): Promise<number> => {
    try {
      // Obtener todas las compras del producto para calcular el costo promedio
      const { data: purchases, error } = await supabase
        .from('purchases')
        .select('final_unit_cost, quantity')
        .eq('product_id', productId)
        .order('purchase_date', { ascending: false });

      if (!error && purchases && purchases.length > 0) {
        // Calcular el costo promedio ponderado por cantidad
        let totalCost = 0;
        let totalQuantity = 0;
        
        purchases.forEach((purchase: any) => {
          totalCost += purchase.final_unit_cost * purchase.quantity;
          totalQuantity += purchase.quantity;
        });
        
        if (totalQuantity > 0) {
          const averageCost = totalCost / totalQuantity;
          console.log(`📊 Costo promedio calculado para producto ${productId}: S/ ${averageCost.toFixed(2)} (${totalQuantity} unidades, ${purchases.length} compras)`);
          return averageCost;
        }
      }

      // Si no hay compras, usar 70% del precio de venta como estimado
      const product = products.find(p => p.id === productId);
      if (product) {
        const estimatedCost = product.price * 0.7;
        console.log(`📊 Costo estimado para producto ${productId}: S/ ${estimatedCost.toFixed(2)} (70% del precio de venta)`);
        return estimatedCost;
      }
      
      return 0;
    } catch (error) {
      console.error('Error al obtener precio de costo:', error);
      return 0;
    }
  };

  const handleAddItem = async () => {
    if (selectedProduct && itemQuantity > 0 && itemSalePrice > 0) {
      const costPrice = await getProductCostPrice(selectedProduct.id);
      const subtotal = itemSalePrice * itemQuantity;
      const totalCost = costPrice * itemQuantity;
      const netProfit = subtotal - totalCost;
      const profitMargin = subtotal > 0 ? (netProfit / subtotal) * 100 : 0;
      
      const newItem: SaleItem = {
        product_id: selectedProduct.id,
        product_name: selectedProduct.name,
        quantity: itemQuantity,
        unit_sale_price: itemSalePrice,
        unit_cost_price: costPrice,
        subtotal,
        total_cost: totalCost,
        net_profit: netProfit,
        profit_margin: profitMargin
      };
      
      setFormData(prev => ({
        ...prev,
        items: [...prev.items, newItem]
      }));
      
      // Resetear formulario de item
      setSelectedProduct(null);
      setItemQuantity(1);
      setItemSalePrice(0);
      setSearchProductTerm('');
      setShowProductSearch(false);
      
      // Mostrar notificación informativa sobre el costo
      if (costPrice > 0) {
        notificationService.info(
          'Costo calculado',
          `Costo unitario promedio: S/ ${costPrice.toFixed(2)} | Ganancia neta: S/ ${netProfit.toFixed(2)} (${profitMargin.toFixed(1)}%)`
        );
      }
    }
  };

  const handleRemoveItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleSelectOrder = async (order: OrderWithDetails) => {
    setShowOrderSearch(false);
    setSearchOrderTerm('');
    await loadOrderData(order.id);
  };

  const handleSelectProduct = (product: Product) => {
    setSelectedProduct(product);
    setItemSalePrice(product.price);
    setShowProductSearch(false);
    setSearchProductTerm('');
  };

  const handleSelectCustomer = (customer: Customer) => {
    setFormData(prev => ({
      ...prev,
      customer_name: customer.name,
      customer_id: customer.id
    }));
    setShowCustomerSearch(false);
    setSearchCustomerTerm('');
  };

  const handlePaymentTypeChange = (paymentType: 'contado' | 'credito') => {
    const creditDays = paymentType === 'contado' ? 0 : formData.credit_days || 30;
    const paymentTerms = paymentType === 'contado' ? 'Contado' : `Crédito a ${creditDays} días`;
    
    setFormData(prev => ({
      ...prev,
      payment_type: paymentType,
      payment_terms: paymentTerms,
      credit_days: creditDays,
      credit_due_date: paymentType === 'contado' ? '' : prev.credit_due_date
    }));
  };

  const handleCreditDaysChange = (creditDays: number) => {
    const paymentTerms = `Crédito a ${creditDays} días`;
    
    setFormData(prev => ({
      ...prev,
      credit_days: creditDays,
      payment_terms: paymentTerms
    }));
  };

  const handleDetraccionToggle = (enabled: boolean) => {
    const subtotal = getTotalSubtotal();
    const detraccionAmount = enabled ? (subtotal * (formData.detraccion_percentage / 100)) : 0;
    
    setFormData(prev => ({
      ...prev,
      detraccion_enabled: enabled,
      detraccion_amount: detraccionAmount
    }));
  };

  const handleDetraccionPercentageChange = (percentage: number) => {
    const subtotal = getTotalSubtotal();
    const detraccionAmount = formData.detraccion_enabled ? (subtotal * (percentage / 100)) : 0;
    
    setFormData(prev => ({
      ...prev,
      detraccion_percentage: percentage,
      detraccion_amount: detraccionAmount
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('🔄 Iniciando envío del formulario de venta');
    console.log('📊 Datos del formulario:', formData);
    
    if (formData.items.length === 0) {
      console.error('❌ Error: No hay items en el formulario');
      notificationService.error('Error', 'Debe agregar al menos un producto a la venta.');
      return;
    }
    
    console.log(`📦 Procesando ${formData.items.length} items`);
    formData.items.forEach((item, index) => {
      console.log(`Item ${index + 1}:`, item);
    });
    
    setIsLoading(true);
    try {
      if (sale) {
        console.log('📝 Modo edición - Actualizando venta existente');
        // Modo edición
        const result = await saleService.updateSale(sale.id, formData);
        console.log('✅ Resultado de actualización:', result);
        notificationService.success('Venta actualizada', 'La venta ha sido actualizada correctamente.');
      } else {
        console.log('➕ Modo creación - Creando nueva venta');
        // Modo creación
        const result = await saleService.addSale(formData);
        console.log('✅ Resultado de creación:', result);
        if (result) {
          notificationService.success('Venta creada', 'La venta ha sido creada correctamente.');
        } else {
          console.error('❌ La venta no se creó (resultado null)');
          notificationService.error('Error', 'No se pudo crear la venta. El servicio devolvió null.');
        }
      }
      onSave();
    } catch (error) {
      console.error('❌ Error al guardar venta:', error);
      notificationService.error('Error', 'No se pudo guardar la venta. Por favor, intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getTotalSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + item.subtotal, 0);
  };

  const getTotalCost = () => {
    return formData.items.reduce((sum, item) => sum + item.total_cost, 0);
  };

  const getTotalNetProfit = () => {
    return formData.items.reduce((sum, item) => sum + item.net_profit, 0);
  };

  const getAverageProfitMargin = () => {
    if (formData.items.length === 0) return 0;
    const totalMargin = formData.items.reduce((sum, item) => sum + item.profit_margin, 0);
    return totalMargin / formData.items.length;
  };

  const getTotalWithIGV = () => {
    const subtotal = getTotalSubtotal();
    const igvAmount = subtotal * 0.18;
    return subtotal + igvAmount;
  };

  const getTotalWithDetraccion = () => {
    const totalWithIGV = getTotalWithIGV();
    return totalWithIGV - formData.detraccion_amount;
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchProductTerm.toLowerCase()) ||
    product.category.toLowerCase().includes(searchProductTerm.toLowerCase())
  );

  const filteredOrders = orders.filter(order =>
    order.order_number.toLowerCase().includes(searchOrderTerm.toLowerCase()) ||
    order.customer_name.toLowerCase().includes(searchOrderTerm.toLowerCase())
  );

  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchCustomerTerm.toLowerCase()) ||
    customer.email.toLowerCase().includes(searchCustomerTerm.toLowerCase()) ||
    (customer.company && customer.company.toLowerCase().includes(searchCustomerTerm.toLowerCase())) ||
    (customer.ruc && customer.ruc.includes(searchCustomerTerm))
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
          {sale ? 'Editar Venta' : 'Nueva Venta'}
        </h2>
        <Button variant="outline" onClick={onCancel}>
          <X className="w-4 h-4 mr-2" />
          Cancelar
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Columna izquierda - Información general */}
          <div className="space-y-4">
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Información General</h3>
              
              {/* Buscar Orden (solo en creación) */}
              {!sale && !orderId && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Buscar Orden de Pedido (opcional)
                  </label>
                  <div className="relative">
                    <div className="flex">
                      <input
                        type="text"
                        placeholder="Buscar por número o cliente..."
                        value={searchOrderTerm}
                        onChange={(e) => setSearchOrderTerm(e.target.value)}
                        onFocus={() => setShowOrderSearch(true)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowOrderSearch(!showOrderSearch)}
                        className="rounded-l-none rounded-r-lg"
                      >
                        <Search className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {showOrderSearch && searchOrderTerm && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredOrders.length === 0 ? (
                          <div className="p-3 text-gray-500">No se encontraron órdenes</div>
                        ) : (
                          filteredOrders.map((order) => (
                            <div
                              key={order.id}
                              onClick={() => handleSelectOrder(order)}
                              className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                            >
                              <div className="font-medium">{order.order_number}</div>
                              <div className="text-sm text-gray-600">{order.customer_name}</div>
                              <div className="text-sm text-gray-600">{order.product.name}</div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Número de Venta
                  </label>
                  <input
                    type="text"
                    value={formData.sale_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, sale_number: e.target.value }))}
                    placeholder="Autogenerado si se deja vacío"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha de Venta
                  </label>
                  <input
                    type="date"
                    value={formData.sale_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, sale_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              {/* Campos de referencia a orden de pedido */}
              {formData.order_id && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-3 bg-blue-50 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-2">
                      Nº Orden de Pedido
                    </label>
                    <input
                      type="text"
                      value={formData.order_number || ''}
                      readOnly
                      className="w-full px-3 py-2 bg-blue-100 border border-blue-300 rounded-lg text-blue-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-2">
                      Fecha Orden de Pedido
                    </label>
                    <input
                      type="date"
                      value={formData.order_date || ''}
                      readOnly
                      className="w-full px-3 py-2 bg-blue-100 border border-blue-300 rounded-lg text-blue-900"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cliente
                </label>
                <div className="relative">
                  <div className="flex">
                    <input
                      type="text"
                      value={searchCustomerTerm || formData.customer_name}
                      onChange={(e) => {
                        setSearchCustomerTerm(e.target.value);
                        if (!e.target.value) {
                          setFormData(prev => ({ ...prev, customer_name: '', customer_id: undefined }));
                        }
                      }}
                      onFocus={() => setShowCustomerSearch(true)}
                      placeholder="Buscar cliente por nombre, email, RUC..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowCustomerSearch(!showCustomerSearch)}
                      className="rounded-l-none rounded-r-lg"
                    >
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {showCustomerSearch && searchCustomerTerm && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredCustomers.length === 0 ? (
                        <div className="p-3 text-gray-500">No se encontraron clientes</div>
                      ) : (
                        filteredCustomers.map((customer) => (
                          <div
                            key={customer.id}
                            onClick={() => handleSelectCustomer(customer)}
                            className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium">{customer.name}</div>
                            {customer.company && <div className="text-sm text-gray-600">{customer.company}</div>}
                            <div className="text-sm text-gray-600">{customer.email}</div>
                            {customer.ruc && <div className="text-sm text-gray-600">RUC: {customer.ruc}</div>}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Moneda
                  </label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="PEN">Soles (PEN)</option>
                    <option value="USD" selected>Dólares (USD)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Método de Pago
                  </label>
                  <select
                    value={formData.payment_method}
                    onChange={(e) => setFormData(prev => ({ ...prev, payment_method: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="Efectivo">Efectivo</option>
                    <option value="Tarjeta">Tarjeta</option>
                    <option value="Transferencia">Transferencia</option>
                    <option value="Yape">Yape</option>
                    <option value="Plin">Plin</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Condición de Pago
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => handlePaymentTypeChange('contado')}
                    className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                      formData.payment_type === 'contado'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    Contado
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePaymentTypeChange('credito')}
                    className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                      formData.payment_type === 'credito'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    Crédito
                  </button>
                </div>
              </div>

              {formData.payment_type === 'credito' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Días de Crédito
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={formData.credit_days}
                      onChange={(e) => handleCreditDaysChange(parseInt(e.target.value) || 30)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required={formData.payment_type === 'credito'}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      Fecha de Vencimiento
                    </label>
                    <input
                      type="date"
                      value={formData.credit_due_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, credit_due_date: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required={formData.payment_type === 'credito'}
                    />
                  </div>
                </>
              )}

              {/* Solo mostrar campos de entrega si no es una venta generada desde una orden */}
              {!formData.order_id && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Días de Entrega
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={formData.delivery_days}
                      onChange={(e) => {
                        const deliveryDays = parseInt(e.target.value) || 5;
                        setFormData(prev => ({ ...prev, delivery_days: deliveryDays }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Tiempo de entrega: {formData.delivery_days} días hábiles después de la orden de compra
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha de Entrega Estimada
                    </label>
                    <input
                      type="date"
                      value={formData.delivery_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, delivery_date: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notas
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  placeholder="Notas adicionales sobre la venta..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Detracciones */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    <Calculator className="w-4 h-4 inline mr-1" />
                    Calcular Detracciones
                  </label>
                  <button
                    type="button"
                    onClick={() => handleDetraccionToggle(!formData.detraccion_enabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.detraccion_enabled ? 'bg-primary-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.detraccion_enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {formData.detraccion_enabled && (
                  <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Porcentaje de Detracción
                      </label>
                      <select
                        value={formData.detraccion_percentage}
                        onChange={(e) => handleDetraccionPercentageChange(parseFloat(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value={4}>4% - Servicios de construcción</option>
                        <option value={10}>10% - Intermediación laboral</option>
                        <option value={8}>8% - Transporte de bienes</option>
                        <option value={6}>6% - Arrendamiento de bienes muebles</option>
                        <option value={12}>12% - Otros servicios</option>
                      </select>
                    </div>
                    <div className="text-sm text-gray-600">
                      Monto de detracción: <span className="font-medium">{formatCurrency(formData.detraccion_amount)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Columna derecha - Productos */}
          <div className="space-y-4">
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Productos</h3>
              
              {/* Agregar producto - Solo mostrar si no hay productos precargados desde una orden */}
              {(!formData.order_id || formData.items.length === 0) && (
                <div className="mb-4 p-4 border border-gray-200 rounded-lg">
                <div className="relative mb-3">
                  <div className="flex">
                    <input
                      type="text"
                      placeholder="Buscar producto..."
                      value={searchProductTerm}
                      onChange={(e) => setSearchProductTerm(e.target.value)}
                      onFocus={() => setShowProductSearch(true)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowProductSearch(!showProductSearch)}
                      className="rounded-l-none rounded-r-lg"
                    >
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {showProductSearch && searchProductTerm && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredProducts.length === 0 ? (
                        <div className="p-3 text-gray-500">No se encontraron productos</div>
                      ) : (
                        filteredProducts.map((product) => (
                          <div
                            key={product.id}
                            onClick={() => handleSelectProduct(product)}
                            className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium">{product.name}</div>
                            <div className="text-sm text-gray-600">{product.category}</div>
                            <div className="text-sm font-medium">{formatCurrency(product.price)}</div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {selectedProduct && (
                  <div className="space-y-3">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="font-medium text-gray-900">{selectedProduct.name}</div>
                      <div className="text-sm text-gray-600">Stock: {selectedProduct.stock}</div>
                      <div className="text-xs text-blue-600">Costo promedio: Calculando...</div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Cantidad
                        </label>
                        <input
                          type="number"
                          min="1"
                          max={selectedProduct.stock}
                          value={itemQuantity}
                          onChange={(e) => setItemQuantity(parseInt(e.target.value) || 1)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Precio Venta
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={itemSalePrice}
                          onChange={(e) => setItemSalePrice(parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          required
                        />
                      </div>
                    </div>

                    <Button
                      type="button"
                      onClick={handleAddItem}
                      className="w-full"
                      disabled={itemQuantity > selectedProduct.stock}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Agregar Producto
                    </Button>
                  </div>
                )}
              </div>
              )}

              {/* Lista de productos agregados */}
              <div className="space-y-2">
                {formData.items.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{item.product_name}</div>
                      <div className="text-sm text-gray-600">
                        {item.quantity} x {formatCurrency(item.unit_sale_price)} = {formatCurrency(item.subtotal)}
                      </div>
                      <div className="text-sm text-green-600 font-medium">
                        Ganancia: {formatCurrency(item.net_profit)} ({item.profit_margin.toFixed(1)}%)
                      </div>
                      <div className="text-xs text-gray-500">
                        Costo unitario: {formatCurrency(item.unit_cost_price)} | Venta: {formatCurrency(item.unit_sale_price)}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveItem(index)}
                      className="text-red-600 hover:text-red-800 hover:bg-red-50 ml-2"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Resumen */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumen de Venta</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">{formatCurrency(getTotalSubtotal())}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Costo Total:</span>
                  <span className="font-medium">{formatCurrency(getTotalCost())}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Ganancia Neta:</span>
                  <span className={`font-bold text-lg ${getTotalNetProfit() >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(getTotalNetProfit())}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Margen Promedio:</span>
                  <span className={`font-bold ${getAverageProfitMargin() >= 15 ? 'text-green-600' : 'text-red-600'}`}>
                    {getAverageProfitMargin().toFixed(2)}%
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">IGV (18%):</span>
                  <span className="font-medium">{formatCurrency(getTotalSubtotal() * 0.18)}</span>
                </div>
                
                {formData.detraccion_enabled && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Detracción ({formData.detraccion_percentage}%):</span>
                    <span className="font-medium text-red-600">-{formatCurrency(formData.detraccion_amount)}</span>
                  </div>
                )}
                
                <div className="border-t pt-3">
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-900">
                      {formData.detraccion_enabled ? 'Total a Pagar:' : 'Total con IGV:'}
                    </span>
                    <span className="font-bold text-xl text-primary-600">
                      {formatCurrency(formData.detraccion_enabled ? getTotalWithDetraccion() : getTotalWithIGV())}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
          >
            <X className="w-4 h-4 mr-2" />
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isLoading || formData.items.length === 0}
            className="btn-primary"
          >
            {isLoading ? (
              <div className="w-4 h-4 animate-spin rounded-full border-b-2 border-white"></div>
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {sale ? 'Actualizar Venta' : 'Crear Venta'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SaleForm;